import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ContrasenaPdfDialog } from "@/components/ContrasenaPdf";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ExpedienteProvider } from "@/context/ExpedienteContext";
import { Spinner } from "@/components/common";
import ErrorBoundary from "@/components/ErrorBoundary";
import UpdateNotifier from "@/components/UpdateNotifier";
import Layout from "@/components/Layout";
import PortalLayout from "@/components/PortalLayout";
import Login from "@/pages/Login";
import Activar from "@/pages/Activar";
import Inicio from "@/pages/Inicio";
import Empleados from "@/pages/Empleados";
import EmpleadoDetalle from "@/pages/EmpleadoDetalle";
import Contrataciones from "@/pages/Contrataciones";
import ContratacionDetalle from "@/pages/ContratacionDetalle";
import Plantillas from "@/pages/Plantillas";
import Documentos from "@/pages/Documentos";
import Importaciones from "@/pages/Importaciones";
import Configuracion from "@/pages/Configuracion";
import Usuarios from "@/pages/Usuarios";
import Expedientes from "@/pages/Expedientes";
import PExpediente from "@/pages/portal/Expediente";
import PHojaVida from "@/pages/portal/HojaVida";
import PDocumentos from "@/pages/portal/Documentos";
import PBeneficiarios from "@/pages/portal/Beneficiarios";
import PPendientes from "@/pages/portal/Pendientes";
import PContratacion from "@/pages/portal/Contratacion";
import PCertificados from "@/pages/portal/Certificados";
import Certificaciones from "@/pages/Certificaciones";
import Verificar from "@/pages/Verificar";
import PVinculacion from "@/pages/portal/Vinculacion";
import PReglamento from "@/pages/portal/Reglamento";
import PLlamados from "@/pages/portal/Llamados";
import Reglamento from "@/pages/Reglamento";
import Duplicados from "@/pages/Duplicados";
import Terminaciones from "@/pages/Terminaciones";
import TerminacionDetalle from "@/pages/TerminacionDetalle";
import { ClienteEmpleados, ClienteEmpleado } from "@/pages/ClientePortal";
import Perfil from "@/pages/Perfil";

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === null)
    return <div className="flex h-screen items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;

  if (!user)
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/activar/:token" element={<Activar />} />
        <Route path="/verificar/:codigo" element={<Verificar />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );

  if (user.role === "empleado")
    return (
      <ExpedienteProvider>
        <Routes>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/verificar/:codigo" element={<Verificar />} />
          <Route path="/" element={<PortalLayout />}>
            <Route index element={<PExpediente />} />
            <Route path="hoja-vida" element={<PHojaVida />} />
            <Route path="vinculacion" element={<PVinculacion />} />
            <Route path="documentos" element={<PDocumentos />} />
            <Route path="beneficiarios" element={<PBeneficiarios />} />
            <Route path="pendientes" element={<PPendientes />} />
            <Route path="contratacion" element={<PContratacion />} />
            <Route path="certificados" element={<PCertificados />} />
            <Route path="reglamento" element={<PReglamento />} />
            <Route path="llamados" element={<PLlamados />} />
            <Route path="perfil" element={<Perfil />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ExpedienteProvider>
    );

  if (user.role === "empresa_cliente")
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/verificar/:codigo" element={<Verificar />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<ClienteEmpleados />} />
          <Route path="empleados/:id" element={<ClienteEmpleado />} />
          <Route path="reglamento" element={<PReglamento />} />
          <Route path="perfil" element={<Perfil />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/verificar/:codigo" element={<Verificar />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Inicio />} />
        <Route path="empleados" element={<Empleados />} />
        <Route path="empleados/duplicados" element={<Duplicados />} />
        <Route path="empleados/:id" element={<EmpleadoDetalle />} />
        <Route path="contrataciones" element={<Contrataciones />} />
        <Route path="contrataciones/terminaciones" element={<Terminaciones />} />
        <Route path="contrataciones/terminaciones/:id" element={<TerminacionDetalle />} />
        <Route path="contrataciones/:id" element={<ContratacionDetalle />} />
        <Route path="certificaciones" element={<Certificaciones />} />
        <Route path="plantillas" element={<Plantillas />} />
        <Route path="expedientes" element={<Expedientes />} />
        <Route path="documentos" element={<Documentos />} />
        <Route path="importaciones" element={<Importaciones />} />
        <Route path="usuarios" element={<Usuarios />} />
        <Route path="configuracion" element={<Configuracion />} />
        <Route path="reglamento" element={<Reglamento />} />
        <Route path="perfil" element={<Perfil />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="bottom-center" richColors />
        <ContrasenaPdfDialog />
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <UpdateNotifier />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
